/*
 * Copyright (C) 2018 - 2023 Entgra (Pvt) Ltd, Inc - All Rights Reserved.
 *
 * Unauthorised copying/redistribution of this file, via any medium is strictly prohibited.
 *
 * Licensed under the Entgra Commercial License, Version 1.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *       https://entgra.io/licenses/entgra-commercial/1.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
import React, { Component } from 'react';
import { withConfigContext } from '../../../../../../../components/ConfigContext';
import { Card, Col, Row, Divider, Button, Typography } from 'antd';
import styles from '../SingleGroup/styles.module.css';
import { withTranslation } from 'react-i18next';
import GroupDevicesModal from '../GroupDevicesModal';
import HtmlComments from '../../../../../../../components/ReactHtmlComments/HtmlComments';
import { Link } from 'react-router-dom';
import { EditOutlined, EnvironmentOutlined } from '@ant-design/icons';
import GroupActions from '../GroupActions';
import ReactHtmlParser from 'react-html-parser';
import ReactQuill from 'react-quill';
import TopGroups from '../TopGroups';

const { Text } = Typography;

const modules = {
  toolbar: [
    ['bold', 'italic', 'underline', 'strike', 'blockquote'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
  ],
};

const formats = [
  'bold',
  'italic',
  'underline',
  'strike',
  'blockquote',
  'list',
  'bullet',
  'link',
];

class Details extends Component {
  constructor(props) {
    super(props);
    this.config = this.props.context;
    this.state = {
      refreshTable: false,
      isDescriptionEditEnabled: false,
      isNameEditEnabled: false,
    };
  }

  componentDidUpdate(prevProps) {
    if (prevProps.group.id !== this.props.group.id) {
      this.setState({ refreshTable: false });
    }
  }

  enableDescriptionEdit = () => {
    this.setState({
      isDescriptionEditEnabled: true,
    });
  };

  enableNameEdit = () => {
    this.setState({
      isNameEditEnabled: true,
    });
  };

  disableNameEdit = () => {
    this.setState({
      isNameEditEnabled: false,
    });
  };

  disableDescriptionEdit = () => {
    this.setState({
      isDescriptionEditEnabled: false,
    });
  };

  refreshDeviceCallback = status => {
    this.setState({ refreshTable: status });
  };

  onSave = () => {
    this.props.handleUpdate();
    this.disableDescriptionEdit();
    this.disableNameEdit();
  };

  render() {
    const { isDescriptionEditEnabled, isNameEditEnabled } = this.state;
    const { t, group, shareRolesData } = this.props;
    const { owner, childrenGroups, name, description } = group;

    const cardStyleName = {
      height: isNameEditEnabled ? '300px' : '100px',
    };

    const cardStyleDescription = {
      height: isDescriptionEditEnabled ? '300px' : '100px',
    };

    const isDefaultGroup = group && (group.id === 1 || group.id === 2);

    return (
      <div>
        <Divider dashed={0}>
          <p className={styles.dividerText}>{name}</p>
        </Divider>
        <Row>
          <Col span={12}>
            <Card style={{ height: '100px' }}>
              <Row>
                <Col md={12}>
                  <div>
                    <Text strong={true}>{t('label_group_owner')}</Text>
                    <div>{owner}</div>
                  </div>
                </Col>
              </Row>
            </Card>
            <Card style={cardStyleName}>
              <Row>
                <Col md={12}>
                  <div>
                    <Text strong={true}>{t('label_groupName')} </Text>
                    {!isDefaultGroup && (
                      <Text
                        style={{
                          color: this.config.theme.primaryColor,
                          cursor: 'pointer',
                        }}
                        onClick={this.enableNameEdit}
                      >
                        <EditOutlined />
                      </Text>
                    )}
                  </div>
                  {!isNameEditEnabled && <div>{ReactHtmlParser(name)}</div>}
                  {isNameEditEnabled && !isDefaultGroup && (
                    <div>
                      <ReactQuill
                        theme="snow"
                        value={this.props.quilName}
                        onChange={this.props.handleNameChange}
                        modules={modules}
                        formats={formats}
                        placeholder={t('label_edit_name')}
                        style={{
                          marginBottom: 10,
                          marginTop: 10,
                          width: '500px',
                        }}
                      />
                      <Button
                        style={{ marginRight: 10 }}
                        size="small"
                        htmlType="button"
                        onClick={this.disableNameEdit}
                      >
                        {t('label_cancel')}
                      </Button>
                      <Button
                        size="small"
                        type="primary"
                        htmlType="button"
                        onClick={this.onSave}
                      >
                        {t('label_save')}
                      </Button>
                    </div>
                  )}
                </Col>
              </Row>
            </Card>
            <Card style={cardStyleDescription}>
              <Row>
                <Col md={12}>
                  <div>
                    <Text strong={true}>{t('label_groupDescription')} </Text>
                    {!isDefaultGroup && (
                      <Text
                        style={{
                          color: this.config.theme.primaryColor,
                          cursor: 'pointer',
                        }}
                        onClick={this.enableDescriptionEdit}
                      >
                        <EditOutlined />
                      </Text>
                    )}
                  </div>
                  {!isDescriptionEditEnabled && (
                    <div>{ReactHtmlParser(description)}</div>
                  )}
                  {isDescriptionEditEnabled && !isDefaultGroup && (
                    <div>
                      <ReactQuill
                        theme="snow"
                        value={this.props.quilDescription}
                        onChange={this.props.handleDescriptionChange}
                        modules={modules}
                        formats={formats}
                        placeholder={t('label_edit_description')}
                        style={{
                          marginBottom: 10,
                          marginTop: 10,
                          width: '500px',
                        }}
                      />
                      <Button
                        style={{ marginRight: 10 }}
                        size="small"
                        htmlType="button"
                        onClick={this.disableDescriptionEdit}
                      >
                        {t('label_cancel')}
                      </Button>
                      <Button
                        size="small"
                        type="primary"
                        htmlType="button"
                        onClick={this.onSave}
                      >
                        {t('label_save')}
                      </Button>
                    </div>
                  )}
                </Col>
              </Row>
            </Card>
          </Col>
          <Col span={12}>
            <Card style={{ height: '100px' }}>
              <Row>
                <Col md={6}>
                  <Text strong={true}>{t('label_actions')}</Text>
                </Col>
                <Col md={6}>
                  {!isDefaultGroup && (
                    <GroupActions
                      group={group}
                      updateShareRolesData={this.props.updateShareRolesData}
                    />
                  )}
                </Col>
                <Col md={6}>
                  <GroupDevicesModal
                    groupData={group}
                    refreshTable={this.state.refreshTable}
                    refreshDeviceCallback={this.refreshDeviceCallback}
                  />
                </Col>
                <Col md={6}>
                  <HtmlComments
                    permission={
                      '/permission/admin/device-mgt/devices/owning-device/view'
                    }
                  />
                  <Link to={`/${this.config.appName}/geo/history/${group.id}`}>
                    <EnvironmentOutlined /> {t('label_devicesHistory')}
                  </Link>
                </Col>
                {!isDescriptionEditEnabled && <div>{ReactHtmlParser(' ')}</div>}
              </Row>
            </Card>
            <Card style={{ height: '100px' }}>
              <Row>
                <Col md={24}>
                  <div>
                    <Text strong={true}>{t('label_assigned_roles')} </Text>
                    {shareRolesData && (
                      <p>
                        {shareRolesData.map((role, index) => (
                          <span key={index}>
                            {role}
                            {index !== shareRolesData.length - 1 && ', '}
                          </span>
                        ))}
                      </p>
                    )}
                  </div>
                </Col>
              </Row>
            </Card>
            <Card style={{ height: '100px' }}>
              <Row>
                <Col md={12}>
                  <div>
                    <Text strong={true}>{t('label_change_group_level')} </Text>
                  </div>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
        <Divider dashed={1} orientation="left">
          {' '}
          <p className={styles.dividerText2}>{t('label_sub_groups')}</p>
        </Divider>
        <p></p>
        <Row>
          <Col md={24}>
            {childrenGroups && (
              <div>
                {/* eslint-disable-next-line react/no-children-prop */}
                <TopGroups children={childrenGroups} />
              </div>
            )}
            {(!childrenGroups || childrenGroups.length === 0) && (
              <div>
                <p>
                  {t('label_no_sub_groups')} {name} {t('home_menu_group')}
                </p>
              </div>
            )}
          </Col>
        </Row>
      </div>
    );
  }
}

export default withConfigContext(withTranslation()(Details));
